import React, { useState, useEffect, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, LineChart, Line
} from 'recharts';
import { operationalDashboardService } from '../../services/operationalDashboard';

interface Props {
  filters: { periodo: string; dataInicio?: string; dataFim?: string };
}

function formatSeconds(s: number): string {
  if (!s || s <= 0) return '-';
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  if (m > 0) return `${m}m ${sec.toString().padStart(2, '0')}s`;
  return `${sec}s`;
}

function formatCurrency(v: number): string {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatAreaLabel(r: any): string {
  if (r.area_codigo && r.area_nome) return `${r.area_codigo} (${r.area_nome})`;
  return r.area_codigo || r.area_nome || '-';
}

const OperationalDashboardRegions: React.FC<Props> = ({ filters }) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableFilters = useMemo(() => filters, [filters.periodo, filters.dataInicio, filters.dataFim]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');
        const res = await operationalDashboardService.getRegions(stableFilters);
        setData(res.data);
      } catch (err: unknown) {
        setError('Erro ao carregar dados de áreas de entrega. Tente novamente.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [stableFilters]);

  if (loading) {
    return <div className="op-dashboard-loading"><div className="op-dashboard-spinner" />Carregando áreas de entrega...</div>;
  }

  if (error) {
    return <div className="op-dashboard-error">{error}</div>;
  }

  if (!data) return null;

  const pedidos = data.pedidos_por_area || [];
  const cargas = data.cargas_por_area || [];
  const paletes = data.paletes_por_dia || [];

  return (
    <>
      {/* Pedidos por Área de Entrega */}
      <h2 className="op-dashboard-section-title">Pedidos por Área de Entrega</h2>
      {pedidos.length > 0 ? (
        <>
          <div className="op-dashboard-chart-container">
            <ResponsiveContainer width="100%" height={Math.min(700, Math.max(300, pedidos.length * 38))}>
              <BarChart data={pedidos.map((r: any) => ({
                area: formatAreaLabel(r),
                pedidos: r.total_pedidos,
                etiquetas: r.total_etiquetas,
                divergentes: r.divergentes
              }))} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.1)" />
                <XAxis type="number" stroke="#94a3b8" />
                <YAxis type="category" dataKey="area" stroke="#94a3b8" width={160} tick={{ fontSize: 12 }} />
                <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid rgba(148,163,184,0.2)', borderRadius: 8, color: '#e2e8f0' }} />
                <Bar dataKey="pedidos" fill="#3b82f6" radius={[0, 4, 4, 0]} name="Pedidos" />
                <Bar dataKey="divergentes" fill="#ef4444" radius={[0, 4, 4, 0]} name="Divergentes" />
                <Legend />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="op-dashboard-card-section">
            <table className="op-dashboard-table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Nome da Área</th>
                  <th>Região</th>
                  <th>Pedidos</th>
                  <th>Etiquetas</th>
                  <th>Divergentes</th>
                  <th>Valor Total</th>
                  <th>Peso (kg)</th>
                </tr>
              </thead>
              <tbody>
                {pedidos.map((r: any, i: number) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 600, fontFamily: 'monospace' }}>{r.area_codigo}</td>
                    <td>{r.area_nome || '-'}</td>
                    <td>{r.area_regiao || '-'}</td>
                    <td>{r.total_pedidos.toLocaleString('pt-BR')}</td>
                    <td>{r.total_etiquetas.toLocaleString('pt-BR')}</td>
                    <td>
                      <span className={`op-dashboard-badge ${r.divergentes > 0 ? 'red' : 'green'}`}>
                        {r.divergentes}
                      </span>
                    </td>
                    <td>{r.valor_total > 0 ? formatCurrency(r.valor_total) : '-'}</td>
                    <td>{r.peso_total > 0 ? r.peso_total.toLocaleString('pt-BR', { maximumFractionDigits: 1 }) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="op-dashboard-empty">Sem dados de pedidos por área de entrega</div>
      )}

      {/* Cargas por Área de Entrega */}
      <h2 className="op-dashboard-section-title">Cargas por Área de Entrega</h2>
      {cargas.length > 0 ? (
        <div className="op-dashboard-card-section">
          <table className="op-dashboard-table">
            <thead>
              <tr>
                <th>Código</th>
                <th>Nome da Área</th>
                <th>Região</th>
                <th>Cargas</th>
                <th>Itens</th>
                <th>Divergências</th>
                <th>Concluídas</th>
                <th>Tempo Médio</th>
              </tr>
            </thead>
            <tbody>
              {cargas.map((r: any, i: number) => (
                <tr key={i}>
                  <td style={{ fontWeight: 600, fontFamily: 'monospace' }}>{r.area_codigo}</td>
                  <td>{r.area_nome || '-'}</td>
                  <td>{r.area_regiao || '-'}</td>
                  <td>{r.total_cargas}</td>
                  <td>{(r.total_itens || 0).toLocaleString('pt-BR')}</td>
                  <td>
                    <span className={`op-dashboard-badge ${r.total_divergencias > 0 ? 'red' : 'green'}`}>
                      {r.total_divergencias}
                    </span>
                  </td>
                  <td>{r.concluidas}</td>
                  <td>{formatSeconds(r.tempo_medio)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="op-dashboard-empty">Sem dados de cargas por área de entrega</div>
      )}

      {/* Paletes por Dia */}
      <h2 className="op-dashboard-section-title">Volume de Paletes por Dia</h2>
      {paletes.length > 0 ? (
        <div className="op-dashboard-chart-container">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={paletes.map((p: any) => ({
              dia: new Date(p.dia).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
              paletes: p.total_paletes,
              operacoes: p.total_operacoes
            }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.1)" />
              <XAxis dataKey="dia" stroke="#94a3b8" />
              <YAxis stroke="#94a3b8" />
              <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid rgba(148,163,184,0.2)', borderRadius: 8, color: '#e2e8f0' }} />
              <Line type="monotone" dataKey="paletes" stroke="#a855f7" strokeWidth={2} name="Paletes" dot={{ r: 3 }} />
              <Line type="monotone" dataKey="operacoes" stroke="#06b6d4" strokeWidth={2} name="Operações" dot={{ r: 3 }} />
              <Legend />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="op-dashboard-empty">Sem dados de paletes no período</div>
      )}
    </>
  );
};

export default OperationalDashboardRegions;
